import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import { ArrowRight, BadgeCheck, DollarSign, FileText, Heart, Phone, Plus, Shield, ShieldCheck, Wrench, Zap } from "lucide-react"; // Lucide icons
import { motion } from "framer-motion";

import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI (Return to Invoice)
  FaTruckPickup, // For Roadside Assistance
  FaCogs, // For Consumables Cover
  FaCheckCircle,
  FaRoad,
  FaGavel,
  FaCloudRain,
  FaCarCrash,
} from "react-icons/fa";

import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";
function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const MarutiSuzukiAltoCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const faqs = [ 
  {
    question: "Is car insurance mandatory for Maruti Suzuki Alto K10?",
    answer: "Yes. At least third-party insurance is mandatory for all cars under Indian law.",
  },
  {
    question: "What is the best type of insurance for Alto K10?",
    answer: "A comprehensive policy is recommended as it covers third-party liability and own-damage protection.",
  },
  {
    question: "How much is the average insurance cost for Alto K10?",
    answer: "A third-party plan costs ₹2,000–₹2,500 annually, while a comprehensive plan is usually between ₹7,000–₹12,000.",
  },
  {
    question: "Can I get zero-depreciation cover for Alto K10?",
    answer: "Yes, insurers offer zero-depreciation cover that allows full claim settlement without depreciation deduction.",
  },
  {
    question: "Will insurance cover engine damage due to floods?",
    answer: "Only if you have an engine protection add-on. Basic policies don’t cover hydrostatic lock or water damage.",
  },
  {
    question: "Is roadside assistance available with Alto K10 insurance?",
    answer: "Yes, insurers offer roadside assistance as an add-on for towing, emergency fuel, flat tyres, and jump-starts.",
  },
  {
    question: "How is the Alto K10’s IDV calculated?",
    answer: "IDV is the market value of the car after depreciation. It influences both premium and claim amount.",
  },
  {
    question: "Can I transfer NCB (No Claim Bonus) if I sell my Alto K10?",
    answer: "Yes, NCB belongs to the policyholder and can be transferred to a new vehicle’s policy.",
  },
  {
    question: "What documents are needed for Alto K10 claim settlement?",
    answer: "Policy copy, RC, driving license, claim form, repair bills, and an FIR (if applicable).",
  },
  {
    question: "Can I renew Alto K10 insurance online?",
    answer: "Yes, you can renew online instantly by entering car details, comparing premiums, and making digital payment.",
  },
  {
    question: "What happens if I don’t renew my insurance on time?",
    answer: "Your policy lapses, you lose NCB benefits, and driving uninsured makes you liable for penalties.",
  },
  {
    question: "Is cashless repair available for Alto K10?",
    answer: "Yes, at insurer network garages, where repair bills are settled directly by the insurer.",
  },
  {
    question: "Can I buy insurance without inspection for Alto K10?",
    answer: "Yes, if renewed on time. Lapsed policies often require physical inspection.",
  },
  {
    question: "Do I get discounts on premium for Alto K10?",
    answer: "Yes, through NCB, anti-theft devices, voluntary deductibles, or automobile association memberships.",
  },
  {
    question: "Can students insure an Alto K10?",
    answer: "Yes, provided the car is in their name and eligibility criteria are met. A guarantor may be required.",
  },
  {
    question: "Is theft of accessories covered?",
    answer: "Only if you opt for an accessories cover add-on.",
  },
  {
    question: "Can NRIs insure their Alto K10 in India?",
    answer: "Yes, NRIs can insure cars registered in India, usually with a local co-applicant or nominee.",
  },
  {
    question: "What is the claim settlement ratio, and why is it important?",
    answer: "It shows the percentage of claims successfully settled by the insurer. Higher ratios mean more reliable service.",
  },
  {
    question: "Can I get long-term car insurance for Alto K10?",
    answer: "Yes, some insurers offer long-term third-party cover bundled with 1-year own-damage cover.",
  },
  {
    question: "Does insurance cover natural disasters like floods or earthquakes?",
    answer: "Yes, a comprehensive policy covers natural calamities such as floods, earthquakes, and cyclones.",
  },
];
 const steps = [
    "Inform the insurer immediately after the accident/theft.",
    "File an FIR in case of theft or major damage.",
    "Submit claim documents (policy copy, RC, DL, FIR, repair bills).",
    "Surveyor inspection of damages.",
    "Cashless repair at network garages or reimbursement option.",
  ];

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description:
        " Get full claim without depreciation deduction.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection Cover",
      description:
        "Covers engine damage due to flooding, oil leakage, etc.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
   
    {
      title: "Roadside Assistance",
      description:
        " Help in case of breakdowns or emergencies.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        " Covers nuts, bolts, oils, and consumables not included in standard policy.",
      icon: <FaCogs className="text-4xl text-green-600 mb-4" />,
    },
     {
      title: "Return to Invoice (RTI)",
      description: " Get the full invoice value in case of total loss/theft.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: " Easy to Use on Any Device" },
    { icon: "💰 ", text: " Helps You Save on Insurance" },
  ];
const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
    }),
  };
  const tableData = [
    {
      type: "Third-Party Only",
      premium: "₹2,000 – ₹2,500",
      color: "orange",
    },
    {
      type: "Comprehensive",
      premium: "₹7,000 – ₹12,000",
      color: "teal",
    },
    {
      type: "Comprehensive + Add-ons",
      premium: "₹10,000 – ₹15,000",
      color: "indigo",
    },
  ];
  return (
    <>
      <Helmet>
        <title>
           Maruti Suzuki Alto k10 Car Insurance in India - Compare Quotes & Buy
          Online
        </title>
        <meta
          name="description"
          content="Explore Maruti Suzuki car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Maruti  vehicle"
        />
        <meta
          name="keywords"
          content="maruti car insurance, auto insurance india, maruti suzuki  insurance, car insurance quotes"
        />
        <link rel="canonical" href="https://thefinmaster.com/maruti-suzuki-alto-car-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      {/* Your existing content */}
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>

          <li className="flex items-center">
            <span className="text-gray-500 ">Maruti Suzuki Alto k10 Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600 ">
                    Maruti Suzuki Alto K10{" "}
                  </span>{" "}
                  Car Insurance Guide
                </h1>
                <p className="text-lg md:text-base lg:text-lg  mt-4 pl-4">
The Maruti Suzuki Alto K10 is one of the most popular entry-level hatchbacks in India, appreciated for its affordability, fuel efficiency, and reliability. Whether you use it for daily commutes or family drives, protecting your Alto K10 with the right car insurance is essential. Car insurance not only provides financial protection against accidents, theft, and natural disasters but also ensures compliance with Indian motor laws.This guide covers everything you need to know about Alto K10 car insurance, including types of policies, key features, add-ons, average premiums, and answers to common questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="/assets/altoma.jpeg"
                alt="Noimage"
              />
            </div>
          </div>
        </div>
      </div>

 

      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>

    <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16">
  <div className="max-w-7xl mx-auto px-6">
    {/* Heading */}
    <h3 className="text-2xl font-bold text-gray-800 flex items-center justify-center mb-12">
      <span className="w-12 h-12 border-2 border-blue-500 rounded-lg flex items-center justify-center mr-3">
        <Shield className="text-blue-600 w-7 h-7" />
      </span>
      Why Car Insurance is Important
    </h3>

    {/* Grid Layout */}
    <div className="grid gap-6 md:grid-cols-4">
      
      {/* Legal Requirement */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-red-500 mb-4">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
        </div>
        <h4 className="text-lg font-semibold text-red-700 mb-2">Legal Requirement</h4>
        <p className="text-red-600 text-sm leading-relaxed">
          Third-party liability insurance is mandatory under the Motor Vehicles Act.
        </p>
      </div>

      {/* Financial Protection */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-green-500 mb-4">
          <DollarSign className="w-6 h-6 text-green-600" />
        </div>
        <h4 className="text-lg font-semibold text-green-700 mb-2">Financial Protection</h4>
        <p className="text-green-600 text-sm leading-relaxed">
          Covers repair costs, medical expenses, and liabilities in case of an accident.
        </p>
      </div>

      {/* Peace of Mind */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-blue-500 mb-4">
          <Heart className="w-6 h-6 text-blue-600" />
        </div>
        <h4 className="text-lg font-semibold text-blue-700 mb-2">Peace of Mind</h4>
        <p className="text-blue-600 text-sm leading-relaxed">
          Comprehensive policies extend protection against theft, fire, and natural calamities.
        </p>
      </div>
 {/* Peace of Mind */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-blue-500 mb-4">
          <ShieldCheck className="w-6 h-6 text-blue-600" />
        </div>
        <h4 className="text-lg font-semibold text-blue-700 mb-2">Enhanced Security with Add-ons</h4>
        <p className="text-blue-600 text-sm leading-relaxed">
          Options like zero-depreciation, engine cover, and roadside assistance ensure wider coverage.
        </p>
      </div>
    </div>
  </div>
</div>
{/* <div className="px-6 mt-6 md:px-14">
<div className=" max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
 
  <h3 className="text-2xl font-bold text-gray-800 mb-10 flex items-center">
    <div className="w-10 h-10 border-2 border-purple-500 rounded-lg flex items-center justify-center mr-3">
      <FileText className="text-purple-600 w-6 h-6" />
    </div>
    Types of Alto K10 Car Insurance Policies
  </h3>


  <div className="space-y-6">
    
   
    <div className="border-l-4 border-orange-500 bg-orange-50 p-6 rounded-r-xl shadow-sm hover:shadow-md transition">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-8 h-8 border border-orange-500 rounded-full bg-white flex items-center justify-center text-orange-600 font-bold text-sm">
          1
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-orange-700 mb-3">Third-Party Liability Insurance</h4>
          <ul className="space-y-2 text-orange-600 text-sm leading-relaxed">
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-3"></span>
              Mandatory under the Motor Vehicles Act.
            </li>
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-3"></span>
              Covers damages to third-party life, property, or vehicle.
            </li>
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-3"></span>
              Does not cover your own car's damage.
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="border-l-4 border-teal-500 bg-teal-50 p-6 rounded-r-xl shadow-sm hover:shadow-md transition">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-8 h-8 border border-teal-500 rounded-full bg-white flex items-center justify-center text-teal-600 font-bold text-sm">
          2
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-teal-700 mb-3">Comprehensive Car Insurance</h4>
          <ul className="space-y-2 text-teal-600 text-sm leading-relaxed">
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 mr-3"></span>
              Covers both third-party liabilities and own damage.
            </li>
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 mr-3"></span>
              Protection against theft, fire, natural/man-made disasters, and accidents.
            </li>
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 mr-3"></span>
              Customizable with add-ons.
            </li>
          </ul>
        </div>
      </div>
    </div>

 
    <div className="border-l-4 border-indigo-500 bg-indigo-50 p-6 rounded-r-xl shadow-sm hover:shadow-md transition">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-8 h-8 border border-indigo-500 rounded-full bg-white flex items-center justify-center text-indigo-600 font-bold text-sm">
          3
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-indigo-700 mb-3">Standalone Own Damage Insurance</h4>
          <ul className="space-y-2 text-indigo-600 text-sm leading-relaxed">
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-2 mr-3"></span>
              For cars already covered by third-party insurance.
            </li>
            <li className="flex items-start">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-2 mr-3"></span>
              Covers damage to your Alto K10 in accidents, fire, theft, etc.
            </li>
          </ul>
        </div>
      </div>
    </div>

  </div>
</div>
</div> */}
<div className="px-6 mt-6 md:px-14">
      <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
        {/* Heading */}
        <motion.h3
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="text-2xl font-bold text-gray-800 mb-10 flex items-center"
        >
          <div className="w-10 h-10 border-2 border-purple-500 rounded-lg flex items-center justify-center mr-3">
            <FileText className="text-purple-600 w-6 h-6" />
          </div>
          Types of Alto K10 Car Insurance Policies
        </motion.h3>

        {/* List of Policies */}
        <div className="space-y-6">
          {[
            {
              id: 1,
              title: "Third-Party Liability Insurance",
              border: "orange",
              points: [
                "Mandatory under the Motor Vehicles Act.",
                "Covers damages to third-party life, property, or vehicle.",
                "Does not cover your own car's damage.",
              ],
            },
            {
              id: 2,
              title: "Comprehensive Car Insurance",
              border: "teal",
              points: [
                "Covers both third-party liabilities and own damage.",
                "Protection against theft, fire, natural/man-made disasters, and accidents.",
                "Customizable with add-ons.",
              ],
            },
            {
              id: 3,
              title: "Standalone Own Damage Insurance",
              border: "blue",
              points: [
                "For cars already covered by third-party insurance.",
                "Covers damage to your Alto K10 in accidents, fire, theft, etc.",
              ],
            },
          ].map((policy, i) => (
            <motion.div
              key={policy.id}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
              whileHover={{ scale: 1.03, transition: { type: "spring", stiffness: 200 } }}
              className={`border-l-4 border-${policy.border}-500 bg-${policy.border}-50 p-6 rounded-r-xl shadow-sm hover:shadow-lg transition`}
            >
              <div className="flex items-start space-x-4">
                <div
                  className={`flex-shrink-0 w-8 h-8 border border-${policy.border}-500 rounded-full bg-white flex items-center justify-center text-${policy.border}-600 font-bold text-sm`}
                >
                  {policy.id}
                </div>
                <div className="flex-1">
                  <h4
                    className={`text-lg font-semibold text-${policy.border}-700 mb-3`}
                  >
                    {policy.title}
                  </h4>
                  <ul
                    className={`space-y-2 text-${policy.border}-600 text-sm leading-relaxed`}
                  >
                    {policy.points.map((point, idx) => (
                      <li key={idx} className="flex items-start">
                        <span
                          className={`w-1.5 h-1.5 bg-${policy.border}-500 rounded-full mt-2 mr-3`}
                        ></span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>

      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          {/* Left - Text and CTA */}
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Right - Feature List with Emojis */}
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
<div className="px-6 mt-10 md:px-14">
      <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
        {/* Heading */}
        <h3 className="text-2xl font-bold text-gray-800 mb-6">
          Average Premium for Maruti Suzuki Alto K10 Car Insurance
        </h3>

        <p className="text-gray-600 text-sm mb-8">
          Premiums vary based on coverage, add-ons, IDV (Insured Declared Value),
          and city of registration.
        </p>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          {/* Table Header */}
          <div className="grid grid-cols-2 bg-gray-100 text-gray-700 font-semibold text-sm">
            <div className="p-4">Policy Type</div>
            <div className="p-4">Annual Premium (Approx.)</div>
          </div>

          {/* Table Rows */}
          {tableData.map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-2 border-t border-gray-200 text-sm"
            >
              <div
                className={`p-4 font-medium text-${row.color}-700 flex items-center`}
              >
                <span
                  className={`w-2 h-2 rounded-full bg-${row.color}-500 mr-2`}
                ></span>
                {row.type}
              </div>
              <div className={`p-4 text-${row.color}-600 font-semibold`}>
                {row.premium}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
     <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="container mx-auto p-6 mt-10">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
        Popular Add-On Covers for Alto K10
        </h2>
        {/* <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Depending on your car model and driving habits, these optional covers
          can be worth considering:
        </p> */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>

    
      <div className="w-full px-6 md:px-14 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto grid gap-10">
  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Factors Affecting Premium
              </span>
            </div>
          
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Minimum Age
                </span> */}
                <span> Age of the vehicle</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Maximum Age
                </span> */}
                <span> Insured Declared Value (IDV)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Income Criteria
                </span> */}
                <span> City of registration</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Credit Score
                </span> */}
                <span> Type of coverage (third-party or comprehensive)</span>
              </div>
                <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Credit Score
                </span> */}
                <span> Add-ons selected


</span>
              </div>
               <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Credit Score
                </span> */}
                <span> No Claim Bonus (NCB)</span>
              </div>
              
            </div>
          </div>


          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Alto K10 Insurance
              </span>
            </div>
            {/* <h3 className="text-xl md:text-xl font-bold  text-gray-800 dark:text-white mb-6">
              Here are a few simple ways to reduce your car insurance cost
              without compromising on coverage
            </h3> */}
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Identity Proof
                </span> */}
                <span>
                  {" "}
                  RC (Registration Certificate) of the car
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Address Proof
                </span> */}
                <span>
                  {" "}
                  Previous insurance policy (if renewal)
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Income Proof
                </span> */}
                <span>
                  {" "}
                 PAN/Aadhaar (for KYC)
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Photograph
                </span> */}
                <span>Passport-size photograph</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                {/* <span className="block font-semibold text-gray-900 dark:text-white">
                  Photograph
                </span> */}
                <span>
                  {" "}
                  Avoid making small claims to preserve your NCB slab.
                </span>
              </div>
            </div>
          </div>

      
        </div>
      </div>
 <div className="px-6 mt-10 md:px-14 mb-4">
      <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
        {/* Heading */}
        <h3 className="text-2xl font-bold text-gray-800 mb-10">
          Alto K10 Car Insurance Claim Process
        </h3>

        {/* Vertical Timeline */}
        <div className="relative">
          {/* Line */}
          <span className="absolute left-4 top-0 bottom-0 w-0.5 bg-purple-200"></span>

          <ul className="space-y-8">
            {steps.map((step, idx) => (
              <li key={idx} className="relative flex items-start">
                {/* Step circle */}
                <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 text-white font-semibold z-10">
                  {idx + 1}
                </span>

                {/* Card */}
                <div className="ml-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-4 flex-1">
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    {step}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 px-2 ">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>

        {/* Main Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Icon */}
            <div className="text-indigo-600 text-5xl">
              <FaRoad />
            </div>

            {/* Text Content - REMAINS UNCHANGED */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
              The Maruti Suzuki Alto K10 is a trusted, budget-friendly car that deserves the right protection. Choosing a comprehensive car insurance policy with suitable add-ons ensures complete peace of mind. 
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
              Whether you’re looking for affordable premiums, wide coverage, or fast claims, TheFinMaster helps you compare plans, find the best deals, and insure your Alto K10 with ease.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default MarutiSuzukiAltoCarInsurance;
